import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useUser } from '../../context/UserContext';
import { getMissions } from '../../services/missionService'; // Need to ensure this can filter by client/status
import invoiceService from '../../services/invoiceService';
import clientService from '../../services/clientService';
import { useI18n } from '../../i18n/I18nContext';
import {
    Container,
    Card,
    Row,
    Col,
    Form,
    Button,
    Table,
    Badge,
    Spinner,
    Alert,
    Step,
    Stepper
} from 'react-bootstrap';
import { FaFileInvoice, FaCalendarAlt, FaCheck, FaArrowRight, FaArrowLeft, FaCalculator } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';

import InvoiceGenerationPopup from '../../components/InvoiceGenerationPopup';

const InvoiceCreate = () => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const { user, refreshUser } = useUser();

    // State
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [filterPeriod, setFilterPeriod] = useState('all'); // all, today, 15days, month, last_month
    const [allMissions, setAllMissions] = useState([]); // Raw data from API
    const [filteredMissions, setFilteredMissions] = useState([]); // Displayed data
    const [selectedMissions, setSelectedMissions] = useState([]); // IDs
    const [currency, setCurrency] = useState('EUR');
    const [showGenerationPopup, setShowGenerationPopup] = useState(false);

    useEffect(() => {
        fetchClients();
    }, []);

    // Filter effect
    useEffect(() => {
        applyFilters();
    }, [filterPeriod, allMissions]);

    // Currency effect - simple logic: take from first selected mission or client default
    useEffect(() => {
        if (selectedMissions.length > 0) {
            const first = allMissions.find(m => m.id === selectedMissions[0]);
            if (first && first.currency) {
                setCurrency(first.currency);
            }
        } else if (selectedClient) {
            setCurrency(selectedClient.preferredCurrency || 'EUR');
        }
    }, [selectedMissions, selectedClient, allMissions]);

    const fetchClients = async () => {
        try {
            const data = await clientService.getAllClients();
            setClients(data);
        } catch (err) {
            console.error("Error fetching clients", err);
            toast.error(t('billing.errors.clientsLoadFailed'));
        }
    };

    const fetchMissions = async (clientId) => {
        setLoading(true);
        try {
            // Fetch ALL available missions for this client
            // We fetch everything and filter client-side for smoother UX
            const raw = await getMissions();

            const eligible = raw.filter(m =>
                m.clientId && m.clientId === parseInt(clientId) &&
                m.status === 'TERMINEE' &&
                !m.invoiceId
            );

            setAllMissions(eligible);
            // Default: select none or all? Let's select none to force user interaction, or all for convenience.
            // User flow: "Select client -> see missions". Maybe select all by default is better.
            setSelectedMissions([]);
        } catch (err) {
            console.error(err);
            toast.error(t('billing.errors.missionsLoadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleClientSelect = (e) => {
        const clientId = e.target.value;
        if (!clientId) {
            setSelectedClient(null);
            setAllMissions([]);
            return;
        }
        const client = clients.find(c => c.id.toString() === clientId);
        setSelectedClient(client);
        if (client) {
            fetchMissions(clientId);
        }
    };

    const applyFilters = () => {
        if (!allMissions.length) {
            setFilteredMissions([]);
            return;
        }

        const now = new Date();
        // Reset time part for accurate date comparison
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        let result = [...allMissions];

        switch (filterPeriod) {
            case 'today':
                result = result.filter(m => {
                    const date = new Date(m.departureDate); // or arrivalDate? or finishedAt? usually departure or arrival.
                    return date.setHours(0, 0, 0, 0) === today.getTime();
                });
                break;
            case '15days':
                // Last 15 days
                const fifteenDaysAgo = new Date(today);
                fifteenDaysAgo.setDate(today.getDate() - 15);
                result = result.filter(m => {
                    const date = new Date(m.departureDate);
                    return date >= fifteenDaysAgo && date <= now;
                });
                break;
            case 'month':
                // Current Month
                result = result.filter(m => {
                    const date = new Date(m.departureDate);
                    return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
                });
                break;
            case 'last_month':
                // Previous Month
                const lastMonth = new Date(today);
                lastMonth.setMonth(today.getMonth() - 1);
                result = result.filter(m => {
                    const date = new Date(m.departureDate);
                    return date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
                });
                break;
            default: // 'all'
                break;
        }
        setFilteredMissions(result);
    };

    const handleGenerate = () => {
        if (selectedMissions.length === 0) {
            toast.error(t('billing.errors.selectOne'));
            return;
        }
        setShowGenerationPopup(true);
    };

    const confirmGeneration = async (legalData) => {
        setLoading(true);
        setShowGenerationPopup(false);
        try {
            const payload = {
                clientId: selectedClient.id,
                transporteurId: user.id || user.transporteurId,
                missionIds: selectedMissions,
                issueDate: new Date().toISOString().split('T')[0],
                currency: currency,
                totalHt: totals.ht,
                totalVat: totals.vat,
                totalTtc: totals.ttc,
                ...legalData
            };

            await invoiceService.createInvoice(payload);
            await refreshUser(); // SaaS Learning: Update profile with these new legal details
            toast.success(t('billing.success.created'));
            navigate('/billing');
        } catch (err) {
            console.error(err);
            toast.error(t('billing.errors.createFailed'));
            setLoading(false);
        }
    };

    const handlePreview = async (legalData) => {
        try {
            const payload = {
                clientId: selectedClient.id,
                transporteurId: user.id || user.transporteurId,
                missionIds: selectedMissions,
                issueDate: new Date().toISOString().split('T')[0],
                currency: currency,
                totalHt: totals.ht,
                totalVat: totals.vat,
                totalTtc: totals.ttc,
                ...legalData
            };

            const blob = await invoiceService.previewInvoice(payload);
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error(err);
            toast.error(t('billing.errors.previewFailed'));
        }
    };

    // Calculate totals for SELECTED missions
    const totals = useMemo(() => {
        const missions = allMissions.filter(m => selectedMissions.includes(m.id));
        const ht = missions.reduce((sum, m) => sum + (m.price || 0), 0);
        // VAT calculation: simple 20% fallback if not present
        const vat = missions.reduce((sum, m) => sum + (m.vatAmount || (m.price * 0.2) || 0), 0);
        return { ht, vat, ttc: ht + vat };
    }, [selectedMissions, allMissions]);

    const toggleMission = (id) => {
        if (selectedMissions.includes(id)) {
            setSelectedMissions(selectedMissions.filter(mId => mId !== id));
        } else {
            // Prevent mixing currencies
            const missionToAdd = allMissions.find(m => m.id === id);
            if (selectedMissions.length > 0) {
                const firstSelected = allMissions.find(m => m.id === selectedMissions[0]);
                if (firstSelected && missionToAdd && firstSelected.currency !== missionToAdd.currency) {
                    toast.error(t('billing.errors.mixedCurrency', "Impossible de sélectionner des missions avec des devises différentes."));
                    return;
                }
            }
            setSelectedMissions([...selectedMissions, id]);
        }
    };

    const toggleAllFiltered = () => {
        const allFilteredIds = filteredMissions.map(m => m.id);
        const allSelected = allFilteredIds.every(id => selectedMissions.includes(id));

        if (allSelected) {
            // Unselect only the visible ones
            setSelectedMissions(selectedMissions.filter(id => !allFilteredIds.includes(id)));
        } else {
            // Filter out missions that have a different currency than the first one
            let targetCurrency = currency;
            if (selectedMissions.length > 0) {
                const firstSelected = allMissions.find(m => m.id === selectedMissions[0]);
                if (firstSelected) targetCurrency = firstSelected.currency;
            } else if (filteredMissions.length > 0) {
                targetCurrency = filteredMissions[0].currency;
            }

            const validFilteredIds = filteredMissions
                .filter(m => m.currency === targetCurrency)
                .map(m => m.id);

            if (validFilteredIds.length < filteredMissions.length) {
               toast.warn(t('billing.warnings.mixedCurrencyFiltered', `Seules les missions en ${targetCurrency} ont été sélectionnées.`));
            }

            // Select valid visible ones (merge unique)
            const newSelection = [...new Set([...selectedMissions, ...validFilteredIds])];
            setSelectedMissions(newSelection);
        }
    };

    // Check if all filtered are selected for the checkbox state
    const isAllFilteredSelected = filteredMissions.length > 0 && filteredMissions.every(m => selectedMissions.includes(m.id));

    return (
        <DashboardLayout>
            <ToastContainer />
            <Container fluid className="px-4 py-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="fw-bold text-dark mb-1">{t('billing.create.title')}</h2>
                        <p className="text-muted mb-0">{t('billing.create.subtitle')}</p>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <div className="text-end me-2 d-none d-md-block">
                            <div className="text-muted small">{t('billing.create.totalSelected')}</div>
                            <div className="fw-bold fs-5 text-primary">{totals.ttc.toFixed(2)} {currency}</div>
                        </div>
                        <Button
                            variant="primary"
                            size="lg"
                            className="px-4 rounded-pill shadow-sm"
                            disabled={selectedMissions.length === 0 || loading}
                            onClick={handleGenerate}
                        >
                            {loading ? <Spinner animation="border" size="sm" /> : <><FaFileInvoice className="me-2" /> {t('billing.create.generate')}</>}
                        </Button>
                    </div>
                </div>

                <Row className="g-4">
                    {/* Sidebar / Filters */}
                    <Col lg={3}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <Card.Body className="p-4">
                                <h5 className="fw-bold mb-4">{t('billing.create.config')}</h5>

                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-bold text-muted small text-uppercase">{t('billing.create.clientLabel')}</Form.Label>
                                    <Form.Select
                                        size="lg"
                                        className="form-select-lg shadow-sm border-0 bg-light"
                                        value={selectedClient?.id || ''}
                                        onChange={handleClientSelect}
                                    >
                                        <option value="">{t('billing.create.clientPlaceholder')}</option>
                                        {clients.map(c => (
                                            <option key={c.id} value={c.id}>{c.entreprise || c.nom}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                {selectedClient && (
                                    <div className="mb-4 fade-in">
                                        <Form.Label className="fw-bold text-muted small text-uppercase">{t('billing.create.periodLabel')}</Form.Label>
                                        <div className="d-flex flex-column gap-2">
                                            {[
                                                { id: 'all', label: t('billing.create.periods.all'), icon: <FaCalendarAlt /> },
                                                { id: 'today', label: t('billing.create.periods.today'), icon: <FaCheck /> },
                                                { id: '15days', label: t('billing.create.periods.fifteenDays'), icon: <FaCalculator /> }, // Icon placeholder
                                                { id: 'month', label: t('billing.create.periods.month'), icon: <FaCalendarAlt /> },
                                                { id: 'last_month', label: t('billing.create.periods.lastMonth'), icon: <FaCalendarAlt /> }
                                            ].map(opt => (
                                                <Button
                                                    key={opt.id}
                                                    variant={filterPeriod === opt.id ? 'primary' : 'light'}
                                                    className={`text-start d-flex align-items-center justify-content-between p-3 rounded-3 border-0 ${filterPeriod === opt.id ? 'shadow-sm' : ''}`}
                                                    onClick={() => setFilterPeriod(opt.id)}
                                                >
                                                    <span className="d-flex align-items-center gap-2">
                                                        {opt.label}
                                                    </span>
                                                    {filterPeriod === opt.id && <FaCheck />}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedClient && (
                                    <Alert variant="info" className="d-flex align-items-center rounded-3 border-0 bg-info bg-opacity-10 text-info mt-auto">
                                        <FaCheck className="me-2" />
                                        <div>
                                            <strong>{t('billing.create.missionsFound', { count: filteredMissions.length })}</strong>
                                        </div>
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Main Content / Table */}
                    <Col lg={9}>
                        <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                            {!selectedClient ? (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5 text-muted">
                                    <FaFileInvoice size={64} className="mb-3 opacity-25" />
                                    <h4>{t('billing.create.emptyClient')}</h4>
                                    <p>{t('billing.create.emptyClientDesc')}</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column h-100">
                                    <div className="table-responsive flex-grow-1">
                                        <Table hover className="align-middle mb-0">
                                            <thead className="bg-light sticky-top" style={{ zIndex: 1 }}>
                                                <tr>
                                                    <th className="ps-4 py-3" style={{ width: '3.125rem' }}>{/* 50px / 16 = 3.125rem */}
                                                        <Form.Check
                                                            checked={isAllFilteredSelected}
                                                            onChange={toggleAllFiltered}
                                                        />
                                                    </th>
                                                    <th className="py-3">{t('billing.create.table.ref')}</th>
                                                    <th className="py-3">{t('billing.create.table.date')}</th>
                                                    <th className="py-3">{t('billing.create.table.route')}</th>
                                                    <th className="py-3 text-end pe-4">{t('billing.create.table.amountHt')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredMissions.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-5 text-muted">
                                                            {t('billing.create.emptyMissions')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredMissions.map(m => (
                                                        <tr
                                                            key={m.id}
                                                            className={selectedMissions.includes(m.id) ? "table-active" : ""}
                                                            onClick={() => toggleMission(m.id)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <td className="ps-4" onClick={(e) => e.stopPropagation()}>
                                                                 <Form.Check
                                                                    checked={selectedMissions.includes(m.id)}
                                                                    onChange={() => toggleMission(m.id)}
                                                                />
                                                            </td>
                                                            <td className="fw-medium">{m.referenceNumber || (m.id && m.id.toString().substring(0, 8))}</td>
                                                            <td>{new Date(m.departureDate).toLocaleDateString()}</td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <span className="text-truncate fw-bold" style={{ maxWidth: '9.375rem' }}>{m.expediteurNom || m.departureLocation}</span>
                                                                    <FaArrowRight className="mx-2 text-muted small" />
                                                                    <span className="text-truncate fw-bold" style={{ maxWidth: '9.375rem' }}>{m.destinataireNom || m.arrivalLocation}</span>
                                                                </div>
                                                            </td>
                                                            <td className="text-end pe-4 fw-bold text-dark">
                                                                {m.price} {m.currency}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Footer Summary in Card */}
                                    <div className="bg-light p-4 border-top">
                                        <Row className="align-items-center">
                                            <Col md={6}>
                                                <div className="text-muted small">
                                                    {t('billing.create.selectionSummary', { selected: selectedMissions.length, total: allMissions.length })}
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="d-flex justify-content-end gap-4">
                                                    <div>
                                                        <div className="text-muted small text-end">{t('billing.create.summary.totalHt')}</div>
                                                        <div className="fw-bold">{totals.ht.toFixed(2)} {currency}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-muted small text-end">{t('billing.create.summary.tva')}</div>
                                                        <div className="fw-bold">{totals.vat.toFixed(2)} {currency}</div>
                                                    </div>
                                                    <div className="ps-3 border-start">
                                                        <div className="text-success small text-end fw-bold">{t('billing.create.summary.netToPay')}</div>
                                                        <div className="fw-bold fs-4 text-success lh-1">{totals.ttc.toFixed(2)} {currency}</div>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Invoice Generation Popup */}
            {selectedClient && (
                <InvoiceGenerationPopup
                    show={showGenerationPopup}
                    handleClose={() => setShowGenerationPopup(false)}
                    client={selectedClient}
                    // SaaS Engineering Best Practice: Use the legal owner (Responsable) of the mission as the seller, not the current user.
                    transporteur={user}
                    totals={totals}
                    onConfirm={confirmGeneration}
                    onPreview={handlePreview}
                />
            )}
        </DashboardLayout>
    );
};

export default InvoiceCreate;
